import {
  AttendanceDashboardResponse,
  AttendanceDashboardRow,
  AttendanceDayStatus
} from 'services/AttendanceTrackingService';
import { buildPresentStatusLabels } from 'utils/attendanceOfficeRules';

export const DEFAULT_FULL_DAY_HOURS = 7;
export const DEFAULT_SHORT_DAY_HOURS = 5;

export const isLeaveAttendanceStatus = (status: AttendanceDayStatus) =>
  status === 'FullDayLeave' || status === 'HalfDayLeave';

export const buildAttendanceStatusLabels = (
  fullDayHoursThreshold = DEFAULT_FULL_DAY_HOURS,
  shortDayHoursThreshold = DEFAULT_SHORT_DAY_HOURS
): Record<AttendanceDayStatus, string> => {
  const present = buildPresentStatusLabels(
    fullDayHoursThreshold,
    shortDayHoursThreshold
  );

  return {
    Present: present.present,
    ShortDay: present.shortDay,
    VeryShort: present.veryShort,
    Missing: present.missing,
    FullDayLeave: present.fullDayLeave,
    HalfDayLeave: present.halfDayLeave
  };
};

export const buildAttendanceStatusLegend = (
  fullDayHoursThreshold = DEFAULT_FULL_DAY_HOURS,
  shortDayHoursThreshold = DEFAULT_SHORT_DAY_HOURS
) => {
  const labels = buildAttendanceStatusLabels(
    fullDayHoursThreshold,
    shortDayHoursThreshold
  );

  return (
    [
      'Present',
      'ShortDay',
      'VeryShort',
      'Missing',
      'FullDayLeave',
      'HalfDayLeave'
    ] as AttendanceDayStatus[]
  ).map((status) => ({
    status,
    label: labels[status]
  }));
};

export const ATTENDANCE_STATUS_COLUMN_LABELS: Record<
  AttendanceDayStatus,
  string
> = {
  Present: 'Present',
  ShortDay: 'Short Day',
  VeryShort: 'Too Short',
  Missing: 'Missing',
  FullDayLeave: 'Full Day Leave',
  HalfDayLeave: 'Half Day Leave'
};

const ATTENDANCE_STATUS_LEGEND_ORDER: AttendanceDayStatus[] = [
  'Present',
  'ShortDay',
  'VeryShort',
  'Missing',
  'FullDayLeave',
  'HalfDayLeave'
];

export const buildAttendanceStatusShortLegend = () =>
  ATTENDANCE_STATUS_LEGEND_ORDER.map((status) => ({
    status,
    label: ATTENDANCE_STATUS_COLUMN_LABELS[status]
  }));

export const parseHoursWorked = (value?: string): number | null => {
  if (!value) return null;

  const match = value.match(/(\d+)\s*h(?:\s*(\d+)\s*m)?/i);
  if (!match) return null;

  const hours = Number.parseInt(match[1], 10);
  const minutes = match[2] ? Number.parseInt(match[2], 10) : 0;

  return hours + minutes / 60;
};

export const resolveAttendanceStatus = (
  row: Pick<AttendanceDashboardRow, 'status' | 'hoursWorked' | 'arrival'>,
  fullDayHoursThreshold = DEFAULT_FULL_DAY_HOURS,
  shortDayHoursThreshold = DEFAULT_SHORT_DAY_HOURS
): AttendanceDayStatus => {
  if (isLeaveAttendanceStatus(row.status)) return row.status;
  if (row.status === 'Missing' || (!row.hoursWorked && !row.arrival)) {
    return 'Missing';
  }

  const hours = parseHoursWorked(row.hoursWorked);
  if (hours === null) return 'Missing';
  if (hours >= fullDayHoursThreshold) return 'Present';
  if (hours >= shortDayHoursThreshold) return 'ShortDay';
  return 'VeryShort';
};

export const getAttendanceStatusClass = (status: AttendanceDayStatus) => {
  switch (status) {
    case 'Present':
      return 'present';
    case 'ShortDay':
      return 'short-day';
    case 'VeryShort':
      return 'very-short';
    case 'Missing':
      return 'missing';
    case 'FullDayLeave':
      return 'full-day-leave';
    case 'HalfDayLeave':
      return 'half-day-leave';
    default:
      return 'missing';
  }
};

export const formatDashboardSubtitle = (
  summary: AttendanceDashboardResponse['summary'] | undefined
) => {
  if (!summary) return 'Loading attendance summary...';
  return `${summary.periodLabel} · ${summary.totalEmployees} employees`;
};

export const formatDashboardLoadedCount = (
  loadedCount: number,
  totalCount: number
) => {
  if (totalCount === 0) return 'Showing 0 records';

  return `Showing ${loadedCount} of ${totalCount} records`;
};

export const formatDashboardPageRange = (
  page: number,
  pageSize: number,
  totalCount: number
) => {
  if (totalCount === 0) return 'Showing 0 employees';

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);
  return `Showing ${start} – ${end} of ${totalCount} employees`;
};
